import { type ChangeEvent, useState } from 'react';

import {
  type FileValidationResult,
  getInvalidFiles,
  hasValidationErrors,
  validateFiles,
} from '../../../drive/s3Validation';
import { ensureTrailingSlash, isDirectory } from '../helpers';

interface UseFileHandlingProps {
  s3Service: {
    objects: Array<{ Key?: string }>;
    uploadFile: (file: File, key?: string, refreshPrefix?: string) => Promise<void>;
    deleteFile: (key: string, refreshPrefix?: string) => Promise<void>;
    deleteDirectory: (directoryKey: string, refreshPrefix?: string) => Promise<void>;
  };
  currentDirectoryPath?: string;
  onFileUploadStart?: (key: string, filename: string) => void;
  onFileUploadComplete?: (key: string) => void;
}

interface UseFileHandlingReturn {
  validationModalOpen: boolean;
  pendingFiles: File[];
  invalidFileResults: FileValidationResult[];
  isUploadingFiles: boolean;
  handleFileUpload: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleDeleteFile: (key: string) => Promise<void>;
  handleValidationModalClose: () => void;
  handleValidationModalConfirm: (correctedFiles: { originalName: string; correctedName: string }[]) => Promise<void>;
}

const createTrimmedFiles = (files: FileList): File[] =>
  Array.from(files).map((file) => {
    const trimmedName = file.name.trim();
    return trimmedName !== file.name ? new File([file], trimmedName, { type: file.type }) : file;
  });

const normalizeDirectoryPath = (directoryPath?: string): string | undefined =>
  directoryPath ? ensureTrailingSlash(directoryPath) : undefined;

const buildS3Key = (fileName: string, directoryPath?: string): string =>
  directoryPath ? `${normalizeDirectoryPath(directoryPath)}${fileName}` : fileName;

const uploadFiles = async (
  files: File[],
  s3Service: UseFileHandlingProps['s3Service'],
  setIsUploadingFiles: (uploading: boolean) => void,
  directoryPath?: string,
  onFileUploadStart?: (key: string, filename: string) => void,
  onFileUploadComplete?: (key: string) => void
) => {
  try {
    setIsUploadingFiles(true);
    const refreshPrefix = normalizeDirectoryPath(directoryPath);

    // Start all uploads immediately and track them in parallel
    const uploadPromises = files.map(async (file) => {
      const s3Key = buildS3Key(file.name, directoryPath);

      // Notify progress tracker that upload is starting
      if (onFileUploadStart) onFileUploadStart(s3Key, file.name);

      try {
        await s3Service.uploadFile(file, s3Key, refreshPrefix);
      } finally {
        // Notify progress tracker that upload is complete
        if (onFileUploadComplete) onFileUploadComplete(s3Key);
      }
    });

    // Wait for all uploads to complete
    await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Upload failed:', error);
  } finally {
    setIsUploadingFiles(false);
  }
};

export const useFileHandling = (props: UseFileHandlingProps): UseFileHandlingReturn => {
  const { s3Service, currentDirectoryPath, onFileUploadStart, onFileUploadComplete } = props;
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [invalidFileResults, setInvalidFileResults] = useState<FileValidationResult[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = createTrimmedFiles(files);
    const existingKeys = (s3Service.objects?.map((obj) => obj.Key).filter(Boolean) as string[]) || [];
    const validationResults = validateFiles(fileArray, existingKeys);

    if (hasValidationErrors(validationResults)) {
      const invalidFiles = getInvalidFiles(validationResults);
      setPendingFiles(fileArray);
      setInvalidFileResults(invalidFiles);
      setValidationModalOpen(true);
      event.target.value = '';
      return;
    }

    await uploadFiles(
      fileArray,
      s3Service,
      setIsUploadingFiles,
      currentDirectoryPath,
      onFileUploadStart,
      onFileUploadComplete
    );
    event.target.value = '';
  };

  const handleDeleteFile = async (key: string) => {
    if (!key) return;

    const isItemDirectory = isDirectory(key);

    try {
      const refreshPrefix = normalizeDirectoryPath(currentDirectoryPath);
      if (isItemDirectory) {
        await s3Service.deleteDirectory(key, refreshPrefix);
      } else {
        await s3Service.deleteFile(key, refreshPrefix);
      }
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleValidationModalClose = () => {
    setValidationModalOpen(false);
    setPendingFiles([]);
    setInvalidFileResults([]);
  };

  const handleValidationModalConfirm = async (correctedFiles: { originalName: string; correctedName: string }[]) => {
    const correctedFileObjects = pendingFiles.map((file) => {
      const correction = correctedFiles.find((cf) => cf.originalName === file.name);
      if (correction) {
        return new File([file], correction.correctedName, { type: file.type });
      }
      return file;
    });

    setValidationModalOpen(false);
    setPendingFiles([]);
    setInvalidFileResults([]);

    await uploadFiles(
      correctedFileObjects,
      s3Service,
      setIsUploadingFiles,
      currentDirectoryPath,
      onFileUploadStart,
      onFileUploadComplete
    );
  };

  return {
    validationModalOpen,
    pendingFiles,
    invalidFileResults,
    isUploadingFiles,
    handleFileUpload,
    handleDeleteFile,
    handleValidationModalClose,
    handleValidationModalConfirm,
  };
};
